import React, { FC, useContext, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import DialogContext from '../Authentication/DialogContext'
import { hideDialog } from '../utils/DialogUtil'
import ErrorMessage, { closeErrorAlert } from '../utils/ErrorMessage'
import DraggableModalDialog from './DraggableModalDialog'

const ConfirmDialog: FC<{ confirmDialogProps: ConfirmDialogProps }> = ({ confirmDialogProps: props }) => {
  const { t } = useTranslation()
  const {
    show,
    backdrop = 'static',
    header = t('confirmDialog.header'),
    body,
    showFooter = true,
    submitText = t('submit'),
    onSubmit,
    onSubmitExtraArgs = [],
    showCancelButton = true,
    cancelText = t('cancel'),
  } = props
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = React.useState(null as any)
  const [showError, setShowError] = React.useState(false)
  const { confirmDialogPropsSetter } = useContext(DialogContext)

  const handleSubmit = (event: any) => {
    setSubmitting(true)
    const formTarget = event.currentTarget
    event.preventDefault()
    if (formTarget.checkValidity() === false) {
      event.stopPropagation()
      setSubmitting(false)
    } else {
      if (onSubmit) {
        onSubmit(...onSubmitExtraArgs)
          .then((message) => {
            if (typeof message === 'string') {
              const confirmDialogProps: ConfirmDialogProps = {
                show: true,
                header: header,
                body: (
                  <div>
                    {message.split('\n').map((item) => (
                      <p>{item}</p>
                    ))}
                  </div>
                ),
                submitText: t('ok'),
                showCancelButton: false,
              }
              hideDialog(confirmDialogPropsSetter)
              confirmDialogPropsSetter(confirmDialogProps)
            } else {
              hideDialog(confirmDialogPropsSetter)
            }
          })
          .catch((error) => {
            setSubmitting(false)
            setError(error.message)
            setShowError(true)
          })
          .finally(() => setSubmitting(false))
      } else {
        hideDialog(confirmDialogPropsSetter)
      }
    }
  }

  return (
    <Modal
      dialogAs={DraggableModalDialog}
      show={show}
      onHide={() => hideDialog(confirmDialogPropsSetter)}
      backdrop={backdrop}
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
      className='modal-dark'>
      <Modal.Header closeButton>
        <Modal.Title id='contained-modal-title-vcenter'>{header}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {body}
        <ErrorMessage error={error} show={showError} onClose={closeErrorAlert(setError, setShowError)} />
      </Modal.Body>
      {showFooter && (
        <Form noValidate onSubmit={handleSubmit}>
          <Modal.Footer>
            <Button type='submit' disabled={submitting} block={true} className='w-auto'>
              {submitting ? (
                <>
                  <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
                  <span className='ml-1'>{submitText}</span>
                </>
              ) : (
                submitText
              )}
            </Button>
            {showCancelButton && (
              <Button variant='outline-danger' onClick={() => hideDialog(confirmDialogPropsSetter)}>
                {cancelText}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  )
}

export type ConfirmDialogProps = {
  show: boolean
  backdrop?: 'static' | true | false
  header?: string | JSX.Element
  body: string | JSX.Element
  showFooter?: boolean
  onSubmit?: (...args: any[]) => Promise<string | void>
  onSubmitExtraArgs?: any[]
  submitText?: string
  showCancelButton?: boolean
  cancelText?: string
}

export default ConfirmDialog
